// Apple-Inspired Error Display Component
// Shows user-friendly error messages with elegant design and recovery options

import React from 'react';
import { ApiError, getUserErrorMessage, shouldShowRetryButton, getErrorActions } from '../../services/errorHandler';
import './ErrorDisplay.css';

interface ErrorDisplayProps {
  error: ApiError | Error | string | null;
  operation?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
  variant?: 'banner' | 'card' | 'inline';
  showDetails?: boolean;
}

interface EmptyStateProps {
  icon?: string;
  title: string;
  description?: string;
  actions?: Array<{
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
    icon?: string;
  }>;
  className?: string;
  compact?: boolean;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  operation,
  onRetry,
  onDismiss,
  className = '',
  variant = 'banner',
  showDetails = false
}) => {
  if (!error) {
    return null;
  }

  // Convert error to ApiError format
  const apiError: ApiError = error instanceof Error 
    ? {
        message: error.message,
        timestamp: new Date().toISOString(),
        retryable: false
      }
    : typeof error === 'string'
    ? {
        message: error,
        timestamp: new Date().toISOString(),
        retryable: false
      }
    : error;

  const displayMessage = getUserErrorMessage(apiError, operation);
  const showRetry = shouldShowRetryButton(apiError) && onRetry;
  const suggestedActions = getErrorActions(apiError);

  const getErrorIcon = () => {
    switch (apiError.statusCode) {
      case 0:
        return '🌐'; // Network error
      case 401:
      case 403:
        return '🔒'; // Auth error
      case 404:
        return '🔍'; // Not found
      case 408:
        return '⏱️'; // Timeout
      case 429:
        return '🚦'; // Rate limit
      case 500:
      case 502:
      case 503:
      case 504:
        return '🔧'; // Server error
      default:
        return '⚠️'; // Generic error
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  return (
    <div className={`error-display ${variant} ${className}`} role="alert">
      <div className="error-content">
        <div className="error-header">
          <span className="error-icon" aria-hidden="true">{getErrorIcon()}</span>
          <div className="error-message">
            <div className="primary-message">{displayMessage}</div>
            {apiError.code && (
              <div className="error-code">Error Code: {apiError.code}</div>
            )}
          </div>
          {onDismiss && (
            <button 
              className="error-dismiss"
              onClick={onDismiss}
              title="Dismiss error"
              aria-label="Dismiss error"
            >
              ×
            </button>
          )}
        </div>

        {showDetails && (
          <div className="error-details">
            <div className="detail-item">
              <span className="detail-label">Time:</span>
              <span className="detail-value">{formatTimestamp(apiError.timestamp)}</span>
            </div>
            {apiError.statusCode && (
              <div className="detail-item">
                <span className="detail-label">Status:</span>
                <span className="detail-value">{apiError.statusCode}</span>
              </div>
            )}
            {apiError.details && (
              <div className="detail-item">
                <span className="detail-label">Details:</span>
                <pre className="detail-value">
                  {JSON.stringify(apiError.details, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

        {(showRetry || suggestedActions.length > 0) && (
          <div className="error-actions">
            {showRetry && (
              <button 
                className="btn btn-primary btn-small"
                onClick={onRetry}
              >
                Try Again
              </button>
            )}
            
            {suggestedActions.length > 0 && (
              <div className="suggested-actions">
                <span className="actions-label">Suggestions:</span>
                <ul className="actions-list">
                  {suggestedActions.map((action, index) => (
                    <li key={index} className="action-item">{action}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Apple-Inspired Empty State Component
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon = '📭',
  title,
  description,
  actions = [],
  className = '',
  compact = false
}) => {
  return (
    <div className={`empty-state ${compact ? 'compact' : ''} ${className}`}>
      <div className="empty-state-icon" aria-hidden="true">{icon}</div>
      <h3 className="empty-state-title">{title}</h3>
      {description && (
        <p className="empty-state-description">{description}</p>
      )}
      {actions.length > 0 && (
        <div className="empty-state-actions">
          {actions.map((action, index) => (
            <button
              key={index}
              className={`empty-state-action ${action.variant || 'primary'}`}
              onClick={action.onClick}
            >
              {action.icon && <span aria-hidden="true">{action.icon}</span>}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Specialized error components with Apple-inspired design
export const ErrorBanner: React.FC<Omit<ErrorDisplayProps, 'variant'>> = (props) => (
  <ErrorDisplay {...props} variant="banner" />
);

export const ErrorCard: React.FC<Omit<ErrorDisplayProps, 'variant'>> = (props) => (
  <ErrorDisplay {...props} variant="card" />
);

export const InlineError: React.FC<Omit<ErrorDisplayProps, 'variant'>> = (props) => (
  <ErrorDisplay {...props} variant="inline" />
);

// Common empty state configurations
export const NoDataFound: React.FC<Omit<EmptyStateProps, 'icon' | 'title'> & { dataType?: string }> = ({ 
  dataType = 'data', 
  ...props 
}) => (
  <EmptyState
    icon="📊"
    title={`No ${dataType} found`}
    description={`There's no ${dataType} to display right now. Try adjusting your filters or adding some ${dataType}.`}
    {...props}
  />
);

export const NetworkError: React.FC<Omit<EmptyStateProps, 'icon' | 'title'>> = (props) => (
  <EmptyState
    icon="🌐"
    title="Connection Problem"
    description="We're having trouble connecting to our servers. Please check your internet connection and try again."
    actions={[
      {
        label: 'Try Again',
        onClick: () => window.location.reload(),
        variant: 'primary',
        icon: '🔄'
      }
    ]}
    {...props}
  />
);

export const LoadingFailed: React.FC<Omit<EmptyStateProps, 'icon' | 'title'> & { onRetry?: () => void }> = ({ 
  onRetry, 
  ...props 
}) => (
  <EmptyState
    icon="⚠️"
    title="Loading Failed"
    description="Something went wrong while loading this content. Please try again."
    actions={onRetry ? [
      {
        label: 'Retry',
        onClick: onRetry,
        variant: 'primary',
        icon: '🔄'
      }
    ] : []}
    {...props}
  />
);

export default ErrorDisplay;